import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { 
  CalendarClock, Edit3, Filter, RefreshCcw, RotateCcw, 
  Save, Search, UserPlus, X, Check, Ban, Clock 
} from "lucide-react";
import { 
  createAdminUser, fetchAdminUsers, updateAdminUser,
  fetchRenewalRequests, approveRenewalRequest, rejectRenewalRequest,
  replyToSupportTicket
} from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";

const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  is_active: true,
  service_plan: "demo",
  service_start_date: "",
  service_end_date: "",
  allowed_licenses: 3,
};
const PAGE_SIZE = 10;
const PLAN_OPTIONS = [
  { value: "demo", label: "Demo", detail: "3 days" },
  { value: "1month", label: "Monthly", detail: "1 month" },
  { value: "3months", label: "Quarterly", detail: "3 months" },
  { value: "6months", label: "Half Yearly", detail: "6 months" },
  { value: "12months", label: "Yearly", detail: "12 months" },
  { value: "custom", label: "Custom", detail: "Manual end date" },
];
const EMPTY_FILTERS = {
  search: "",
  status: "",
  service_end_from: "",
  service_end_to: "",
  created_from: "",
  created_to: "",
};

const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : format(date, "yyyy-MM-dd");
};

const addMonths = (date, months) => {
  const next = new Date(date);
  const day = next.getDate();
  next.setMonth(next.getMonth() + months);
  if (next.getDate() !== day) next.setDate(0);
  return next;
};

const calculateEndDate = (plan, startValue, customEndValue = "") => {
  const start = startValue ? new Date(`${startValue}T00:00:00`) : new Date();
  if (Number.isNaN(start.getTime())) return "";
  if (plan === "custom") return customEndValue;
  if (plan === "demo") {
    const next = new Date(start);
    next.setDate(next.getDate() + 3);
    return format(next, "yyyy-MM-dd");
  }
  const months = { "1month": 1, "3months": 3, "6months": 6, "12months": 12 }[plan];
  return months ? format(addMonths(start, months), "yyyy-MM-dd") : "";
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active_users: 0, inactive_users: 0 });
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [form, setForm] = useState(EMPTY_FORM);
  const [modalMode, setModalMode] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeLicensePopupUser, setActiveLicensePopupUser] = useState(null);

  const location = useLocation();
  // Tabs state: "users" or "renewals"
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || "users");
  const [renewalRequests, setRenewalRequests] = useState([]);
  const [loadingRenewals, setLoadingRenewals] = useState(false);

  // Support ticket reply modal state
  const [replyTicket, setReplyTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [replyStatus, setReplyStatus] = useState("resolved");
  const [sendingReply, setSendingReply] = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = (nextPage = page, nextFilters = filters) => {
    setLoading(true);
    fetchAdminUsers(nextPage * PAGE_SIZE, PAGE_SIZE, nextFilters)
      .then((data) => {
        setUsers(data.users || []);
        setPage(nextPage);
        setSummary({
          total: data.total || 0,
          active_users: data.active_users || 0,
          inactive_users: data.inactive_users || 0,
          expired_users: data.expired_users || 0,
          page_count: data.page_count || 0,
        });
      })
      .catch((err) => showToast(err.response?.data?.detail || "Users load failed", "error"))
      .finally(() => setLoading(false));
  };

  const loadRenewals = () => {
    setLoadingRenewals(true);
    fetchRenewalRequests()
      .then((data) => {
        setRenewalRequests(data.requests || []);
      })
      .catch((err) => showToast(err.response?.data?.detail || "Failed to load renewal requests", "error"))
      .finally(() => setLoadingRenewals(false));
  };

  useEffect(() => {
    if (user?.role === "admin") {
      if (activeTab === "users") {
        load(0);
      } else {
        loadRenewals();
      }
    }
  }, [user?.role, activeTab]);

  const serviceEndPreview = useMemo(() => (
    calculateEndDate(form.service_plan, form.service_start_date, form.service_end_date)
  ), [form.service_plan, form.service_start_date, form.service_end_date]);

  const hasFilters = useMemo(() => (
    Object.values(filters).some((value) => Boolean(value))
  ), [filters]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    load(0, filters);
  };

  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    load(0, EMPTY_FILTERS);
  };

  const openCreateModal = () => {
    const today = format(new Date(), "yyyy-MM-dd");
    setEditingUser(null);
    setForm({ ...EMPTY_FORM, service_start_date: today, service_end_date: calculateEndDate("demo", today), allowed_licenses: 3 });
    setModalMode("create");
  };

  const openEditModal = (item) => {
    const start = toDateInput(item.service_start_date) || format(new Date(), "yyyy-MM-dd");
    const end = toDateInput(item.service_end_date);
    setEditingUser(item);
    setForm({
      name: item.name || "",
      email: item.email || "",
      password: "",
      is_active: Boolean(item.is_active),
      service_plan: item.service_plan || "custom",
      service_start_date: start,
      service_end_date: end,
      allowed_licenses: item.allowed_licenses ?? 3,
    });
    setModalMode("edit");
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingUser(null);
    setForm(EMPTY_FORM);
  };

  const updateForm = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "service_plan" || key === "service_start_date") {
        next.service_end_date = calculateEndDate(next.service_plan, next.service_start_date, next.service_end_date);
      }
      return next;
    });
  };

  const renderDate = (value, pattern = "MM/dd/yyyy") => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : format(date, pattern);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const serviceEnd = calculateEndDate(form.service_plan, form.service_start_date, form.service_end_date);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        role: "user",
        is_active: form.is_active,
        service_plan: form.service_plan,
        service_start_date: form.service_start_date ? new Date(form.service_start_date).toISOString() : null,
        service_end_date: serviceEnd ? new Date(serviceEnd).toISOString() : null,
        allowed_licenses: Number(form.allowed_licenses || 3),
      };
      if (modalMode === "create" || form.password) payload.password = form.password;

      if (modalMode === "edit") {
        await updateAdminUser(editingUser.id, payload);
        showToast("User updated successfully");
        closeModal();
        load(page);
      } else {
        await createAdminUser(payload);
        showToast("User created successfully");
        closeModal();
        load(0);
      }
    } catch (err) {
      showToast(err.response?.data?.detail || "User save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleApproveRenewal = (id) => {
    const req = renewalRequests.find(r => r.id === id);
    if (!req) return;
    setReplyTicket(req);
    setReplyStatus("resolved");
    setReplyMessage("Dear client, we have successfully resolved your support ticket concerning '" + req.subject + "'.\n\nIf you have any further questions or need additional assistance, please feel free to open a new support request.\n\nBest regards,\nSupport Team");
  };

  const handleRejectRenewal = (id) => {
    const req = renewalRequests.find(r => r.id === id);
    if (!req) return;
    setReplyTicket(req);
    setReplyStatus("closed");
    setReplyMessage("Dear client, we have resolved and closed this support ticket concerning '" + req.subject + "'.\n\nIf this issue persists, or if you have any other questions, please feel free to raise another ticket.\n\nBest regards,\nSupport Team");
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim()) {
      showToast("Please enter a reply message", "error");
      return;
    }
    setSendingReply(true);
    try {
      const data = await replyToSupportTicket(replyTicket.id, {
        message: replyMessage.trim(),
        status: replyStatus
      });
      showToast(data.message || "Reply sent successfully!");
      setReplyTicket(null);
      loadRenewals();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to submit ticket reply", "error");
    } finally {
      setSendingReply(false);
    }
  };

  if (user?.role !== "admin") {
    return (
      <div className="empty">
        Admin access required.
      </div>
    );
  }

  return (
    <div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

      <div className="page-header">
        <div>
          <div className="page-title">Administration Panel</div>
          <div className="page-sub">Manage platform clients, credentials, license plans, and subscription renewal requests</div>
        </div>
        <div className="header-actions">
          <button className="btn btn-ghost" onClick={() => activeTab === "users" ? load(page) : loadRenewals()} disabled={loading || loadingRenewals} title="Refresh records">
            <RefreshCcw size={15} />
            Refresh
          </button>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <UserPlus size={15} />
            New User
          </button>
        </div>
      </div>

      {/* Tabs Row */}
      <div style={{ display: "flex", gap: "10px", marginBottom: "20px", borderBottom: "1px solid var(--border)", paddingBottom: "10px" }}>
        <button 
          onClick={() => setActiveTab("users")}
          className={`btn ${activeTab === "users" ? "btn-primary" : "btn-ghost"}`}
          style={{ minHeight: "auto", padding: "8px 16px" }}
        >
          👤 Users Directory
        </button>
        <button 
          onClick={() => setActiveTab("renewals")}
          className={`btn ${activeTab === "renewals" ? "btn-primary" : "btn-ghost"}`}
          style={{ minHeight: "auto", padding: "8px 16px" }}
        >
          🎫 Ticket Raised
          {renewalRequests.filter(r => r.status === "pending").length > 0 && (
            <span style={{ 
              background: "var(--red)", 
              color: "#fff", 
              borderRadius: "50%", 
              fontSize: "10px", 
              padding: "2px 6px",
              marginLeft: "6px",
              fontWeight: 800,
              boxShadow: "0 0 8px var(--red)"
            }}>
              {renewalRequests.filter(r => r.status === "pending").length}
            </span>
          )}
        </button>
      </div>

      {activeTab === "users" ? (
        <>
          <form className="card filter-card" onSubmit={applyFilters}>
            <div className="card-header">
              <span>Filters</span>
              <Filter size={16} />
            </div>
            <div className="filter-grid">
              <div className="form-group" style={{ flex: "1 1 0" }}>
                <label className="form-label">Search</label>
                <div className="input-with-icon">
                  <Search size={14} />
                  <input
                    className="form-input"
                    value={filters.search}
                    onChange={(e) => updateFilter("search", e.target.value)}
                    placeholder="Name, email, license"
                  />
                </div>
              </div>
              <div className="form-group" style={{ flex: "1 1 0" }}>
                <label className="form-label">Status</label>
                <select
                  className="form-input"
                  value={filters.status}
                  onChange={(e) => updateFilter("status", e.target.value)}
                >
                  <option value="">All status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: "1 1 0" }}>
                <label className="form-label">Service End From</label>
                <input
                  className="form-input"
                  type="date"
                  value={filters.service_end_from}
                  onChange={(e) => updateFilter("service_end_from", e.target.value)}
                />
              </div>
              <div className="form-group" style={{ flex: "1 1 0" }}>
                <label className="form-label">Service End To</label>
                <input
                  className="form-input"
                  type="date"
                  value={filters.service_end_to}
                  onChange={(e) => updateFilter("service_end_to", e.target.value)}
                />
              </div>
              <div className="form-group" style={{ flex: "1 1 0" }}>
                <label className="form-label">Created From</label>
                <input
                  className="form-input"
                  type="date"
                  value={filters.created_from}
                  onChange={(e) => updateFilter("created_from", e.target.value)}
                />
              </div>
              <div className="form-group" style={{ flex: "1 1 0" }}>
                <label className="form-label">Created To</label>
                <input
                  className="form-input"
                  type="date"
                  value={filters.created_to}
                  onChange={(e) => updateFilter("created_to", e.target.value)}
                />
              </div>
              <div className="filter-actions">
                <button className="btn btn-primary" type="submit" disabled={loading}>
                  <Filter size={15} />
                  Apply
                </button>
                <button className="btn btn-ghost" type="button" onClick={resetFilters} disabled={loading || !hasFilters}>
                  <RotateCcw size={15} />
                  Reset
                </button>
              </div>
            </div>
          </form>

          <div className="card">
            <div className="card-header">
              <span>All Registered Clients</span>
              <span style={{ color: "#8b949e", fontSize: 12 }}>
                Page {summary.page_count ? page + 1 : 0} of {summary.page_count || 0}
              </span>
            </div>
            {loading ? (
              <div className="loading">Loading user records...</div>
            ) : users.length === 0 ? (
              <div className="empty">{hasFilters ? "No users match these filters." : "No users available."}</div>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Plan</th>
                      <th>License IDs</th>
                      <th>Service Start</th>
                      <th>Service End</th>
                      <th>Trades Copied</th>
                      <th>Signals Received</th>
                      <th>Active Accounts</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((item) => (
                      <tr key={item.id}>
                        <td style={{ fontWeight: 600 }}>{item.name || "-"}</td>
                        <td>{item.email || "-"}</td>
                        <td>
                          <span className={`badge ${item.status === "expired" ? "badge-error" : item.is_active ? "badge-ok" : "badge-closed"}`}>
                            {item.status}
                          </span>
                        </td>
                        <td>{PLAN_OPTIONS.find((plan) => plan.value === item.service_plan)?.label || item.service_plan || "-"}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            style={{ padding: "5px 10px", fontSize: 11, minHeight: "auto", fontWeight: 700 }}
                            onClick={() => setActiveLicensePopupUser(item)}
                          >
                            🔑 View ({item.license_ids?.length || 0})
                          </button>
                        </td>
                        <td>{renderDate(item.service_start_date)}</td>
                        <td>{renderDate(item.service_end_date)}</td>
                        <td>{item.trades_copied}</td>
                        <td>{item.signals_received}</td>
                        <td>{item.active_accounts}</td>
                        <td>{renderDate(item.last_login, "MM/dd HH:mm")}</td>
                        <td>
                          <button className="icon-btn table-action" type="button" onClick={() => openEditModal(item)} title="Edit user">
                            <Edit3 size={15} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {summary.page_count > 1 && (
              <div className="pagination-row">
                <button className="btn btn-ghost" onClick={() => load(page - 1)} disabled={loading || page === 0}>
                  Previous
                </button>
                <button className="btn btn-ghost" onClick={() => load(page + 1)} disabled={loading || page + 1 >= summary.page_count}>
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Raised Tickets Panel */
        <div className="card">
          <div className="card-header">
            <span>Raised Support Tickets Directory</span>
            <span style={{ color: "#8b949e", fontSize: 12 }}>
              Total Tickets: {renewalRequests.length}
            </span>
          </div>
          {loadingRenewals ? (
            <div className="loading">Loading tickets database...</div>
          ) : renewalRequests.length === 0 ? (
            <div className="empty">No support tickets submitted yet.</div>
          ) : (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr style={{ background: "rgba(0, 242, 254, 0.03)" }}>
                    <th>Raised At</th>
                    <th>User Name</th>
                    <th>User Email</th>
                    <th>Category</th>
                    <th>Subject &amp; Issue Details</th>
                    <th>Status</th>
                    <th style={{ textAlign: "center", width: "200px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {renewalRequests.map((req) => (
                    <tr key={req.id}>
                      <td style={{ color: "var(--text2)", fontSize: "12.5px" }}>
                        {renderDate(req.requested_at, "MM/dd/yyyy HH:mm:ss")}
                      </td>
                      <td style={{ fontWeight: 700, color: "#fff" }}>{req.user_name}</td>
                      <td>{req.user_email}</td>
                      <td style={{ fontWeight: 700, color: "var(--accent)", textTransform: "capitalize" }}>
                        {(req.plan || "other").replace("_", " ")}
                      </td>
                      <td style={{ maxWidth: "350px", wordBreak: "break-word" }}>
                        <div style={{ fontWeight: 800, color: "var(--text)", marginBottom: "4px" }}>{req.subject}</div>
                        <div style={{ fontSize: "11px", color: "var(--text2)", whiteSpace: "pre-line", lineHeight: "1.4" }}>{req.message}</div>
                      </td>
                      <td>
                        <span className={`badge ${
                          req.status === "resolved" 
                            ? "badge-ok" 
                            : req.status === "closed" 
                              ? "badge-error" 
                              : "badge-pending"
                        }`} style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          {req.status === "resolved" && <Check size={12} />}
                          {req.status === "closed" && <Ban size={12} />}
                          {req.status === "pending" && <Clock size={12} />}
                          {req.status.toUpperCase()}
                        </span>
                      </td>
                      <td style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                        {req.status === "pending" ? (
                          <>
                            <button 
                              className="btn btn-ghost" 
                              style={{ 
                                padding: "6px 12px", 
                                fontSize: "11px", 
                                minHeight: "auto",
                                color: "var(--green)",
                                borderColor: "rgba(16, 185, 129, 0.25)",
                                fontWeight: "bold"
                              }}
                              onClick={() => handleApproveRenewal(req.id)}
                            >
                              Resolve
                            </button>
                            <button 
                              className="btn btn-ghost" 
                              style={{ 
                                padding: "6px 12px", 
                                fontSize: "11px", 
                                minHeight: "auto",
                                color: "var(--red)",
                                borderColor: "rgba(239, 68, 68, 0.25)",
                                fontWeight: "bold"
                              }}
                              onClick={() => handleRejectRenewal(req.id)}
                            >
                              Close
                            </button>
                          </>
                        ) : (
                          <span style={{ color: "var(--text2)", fontSize: "12px", fontStyle: "italic" }}>
                            Processed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {modalMode && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeModal();
        }}>
          <form className="user-modal" onSubmit={handleSubmit}>
            <div className="modal-header">
              <div>
                <div className="modal-title">{modalMode === "edit" ? "Edit User" : "Create User"}</div>
                <div className="modal-sub">
                  {modalMode === "edit" ? "Update account, plan, and service dates" : "Create account with automatic service validity"}
                </div>
              </div>
              <button className="icon-btn" type="button" onClick={closeModal} title="Close">
                <X size={18} />
              </button>
            </div>

            <div className="modal-body">
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input
                    className="form-input"
                    value={form.name}
                    onChange={(e) => updateForm("name", e.target.value)}
                    placeholder="Full name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    className="form-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => updateForm("email", e.target.value)}
                    placeholder="user@example.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{modalMode === "edit" ? "New Password" : "Password"}</label>
                  <input
                    className="form-input"
                    type="password"
                    value={form.password}
                    onChange={(e) => updateForm("password", e.target.value)}
                    placeholder={modalMode === "edit" ? "Leave blank to keep current" : "Minimum 6 characters"}
                    minLength={6}
                    required={modalMode === "create"}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Allowed Licenses</label>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    max="100"
                    value={form.allowed_licenses}
                    onChange={(e) => updateForm("allowed_licenses", e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="plan-panel">
                <div className="plan-panel-head">
                  <div>
                    <div className="section-title">Service Plan</div>
                    <div className="modal-sub">Plan select karte hi dates auto-calculate hongi</div>
                  </div>
                  <CalendarClock size={18} />
                </div>
                <div className="plan-grid">
                  {PLAN_OPTIONS.map((plan) => {
                    const disabled = modalMode === "edit" && plan.value === "demo" && editingUser?.demo_used && editingUser?.service_plan !== "demo";
                    return (
                      <label className={`plan-option ${form.service_plan === plan.value ? "active" : ""} ${disabled ? "disabled" : ""}`} key={plan.value}>
                        <input
                          type="radio"
                          name="service_plan"
                          value={plan.value}
                          checked={form.service_plan === plan.value}
                          disabled={disabled}
                          onChange={(e) => updateForm("service_plan", e.target.value)}
                        />
                        <span>{plan.label}</span>
                        <small>{disabled ? "Demo used" : plan.detail}</small>
                      </label>
                    );
                  })}
                </div>

                <div className="grid-3 service-date-grid">
                  <div className="form-group">
                    <label className="form-label">Service Start</label>
                    <input
                      className="form-input"
                      type="date"
                      value={form.service_start_date}
                      onChange={(e) => updateForm("service_start_date", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Service End</label>
                    <input
                      className="form-input"
                      type="date"
                      value={serviceEndPreview}
                      disabled={form.service_plan !== "custom"}
                      onChange={(e) => updateForm("service_end_date", e.target.value)}
                    />
                  </div>
                  <label className="toggle-row compact" style={{ cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => updateForm("is_active", e.target.checked)}
                    />
                    <span>Active user</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-ghost" type="button" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                <Save size={15} />
                {saving ? "Saving..." : modalMode === "edit" ? "Save Changes" : "Create User"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sleek Modal popup for User License IDs */}
      {activeLicensePopupUser && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(e) => {
          if (e.target === e.currentTarget) setActiveLicensePopupUser(null);
        }}>
          <div className="user-modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">License IDs</div>
                <div className="modal-sub">Associated licenses for {activeLicensePopupUser.name}</div>
              </div>
              <button className="icon-btn" type="button" onClick={() => setActiveLicensePopupUser(null)} title="Close">
                <X size={18} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              <div className="stack-list" style={{ padding: 0, gap: 10 }}>
                {(activeLicensePopupUser.license_ids || []).map((lic) => (
                  <div key={lic} className="license-row" style={{ background: "var(--bg3)", padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <code style={{ fontSize: 12 }}>{lic}</code>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ padding: "4px 8px", fontSize: 11 }}
                      onClick={() => {
                        navigator.clipboard.writeText(lic);
                        showToast("Copied to clipboard!");
                      }}
                    >
                      📋 Copy
                    </button>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 20, fontSize: 12.5, color: "var(--text2)", textAlign: "center", fontWeight: 500 }}>
                Limit: {activeLicensePopupUser.allowed_licenses || 3} licenses allowed.
              </div>
            </div>
            <div className="modal-footer" style={{ padding: "12px 20px" }}>
              <button className="btn btn-ghost" type="button" onClick={() => setActiveLicensePopupUser(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Sleek modal popup for Support Ticket Reply */}
      {replyTicket && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(e) => {
          if (e.target === e.currentTarget) setReplyTicket(null);
        }}>
          <form className="user-modal" onSubmit={handleSendReply} style={{ maxWidth: 550 }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Reply to Support Ticket</div>
                <div className="modal-sub">Send developer revert to {replyTicket.user_name} ({replyTicket.user_email})</div>
              </div>
              <button className="icon-btn" type="button" onClick={() => setReplyTicket(null)} title="Close">
                <X size={18} />
              </button>
            </div>
            
            <div className="modal-body" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Ticket Summary Details */}
              <div style={{ background: "rgba(0,0,0,0.2)", borderRadius: "var(--radius)", padding: "12px 16px", border: "1px solid var(--border)" }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", color: "var(--text2)", fontWeight: 700, marginBottom: 4 }}>Original Inquiry</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "var(--text)", marginBottom: 4 }}>{replyTicket.subject}</div>
                <div style={{ fontSize: 12, color: "var(--text2)", whiteSpace: "pre-line", lineHeight: 1.4 }}>{replyTicket.message}</div>
              </div>

              {/* Status Selector */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Update Ticket Status</label>
                <select
                  className="form-input"
                  value={replyStatus}
                  onChange={e => setReplyStatus(e.target.value)}
                  style={{ background: "var(--bg-dark)", color: "var(--text)", border: "1px solid var(--border)", outline: "none", cursor: "pointer" }}
                >
                  <option value="pending">Keep Status: PENDING</option>
                  <option value="resolved">Update Status: RESOLVED (Recommended)</option>
                  <option value="closed">Update Status: CLOSED</option>
                </select>
              </div>

              {/* Reply Message text */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Developer Revert Response</label>
                <textarea
                  className="form-input"
                  placeholder="Draft your reply message..."
                  value={replyMessage}
                  onChange={e => setReplyMessage(e.target.value)}
                  style={{ minHeight: "130px", resize: "vertical", lineHeight: "1.5" }}
                  required
                />
                <span style={{ fontSize: "11px", color: "var(--text2)", marginTop: "4px", display: "block" }}>
                  💡 This response will be pushed instantly to the client's notification hub and unread bell alerts.
                </span>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: "12px 20px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button className="btn btn-ghost" type="button" onClick={() => setReplyTicket(null)}>Cancel</button>
              <button className="btn btn-primary" type="submit" disabled={sendingReply}>
                {sendingReply ? "Sending..." : "Submit Response"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
